// ─────────────────────────────────────────────
//  myRooms — resolve which assignment rows belong to the logged-in user.
//
//  The bug this fixes: assignment.attendingId / ahpId hold the STAFF doc id
//  (set by the sheet/PDF importer and the grid), but the logged-in user's
//  user.uid is the AUTH uid. Those are different ids, and a staff doc's uid
//  link may not be set yet — so matching ahpId === user.uid found nothing and
//  "My Room" showed "Not assigned yet" even when rooms were assigned.
//
//  Match strategy (bridges the gap): collect every staff id that belongs to the
//  user (uid link OR exact name), include user.uid itself, and also match by the
//  name stored on the assignment. Role-aware:
//    • Doctor / AIC / Admin → every room they supervise (attending or MD relief)
//    • CRNA / AA / Resident  → their room(s) (ahp or CRNA relief)
// ─────────────────────────────────────────────
import { accessLevelOf } from "../config/roles";

// Honorific-insensitive name key: a `users` doc often stores "Dr. Gonzalo
// Martinez" while staff/assignments store "Gonzalo Martinez". Strip a leading
// "Dr." so the two match. THIS was the bug — "Dr." broke every name link.
const norm = (s) =>
  String(s || "").trim().toLowerCase().replace(/^dr\.?\s+/, "");
// Provider short code key (e.g. "MAR") — the stable id that survives the
// uid/staff-id/name fragmentation.
const code = (s) => String(s || "").trim().toUpperCase();

// Staff ids that belong to this user — by uid link, honorific-insensitive name,
// OR short code. user.uid is included so assignments that store the auth uid
// match too.
export function userStaffIds(user, staffList = []) {
  const uname = norm(user && user.name);
  const ucode = code(user && user.shortCode);
  const ids = new Set();
  (staffList || []).forEach((s) => {
    const byUid = s.uid && user && s.uid === user.uid;
    const byName = uname && norm(s.name) === uname;
    const byCode = ucode && code(s.shortCode) === ucode;
    if (byUid || byName || byCode) ids.add(s.id);
  });
  if (user && user.uid) ids.add(user.uid);
  return ids;
}

// Is this user a doctor-side viewer (sees every room they supervise)?
export function isDoctorViewer(user) {
  const level = accessLevelOf(user || {});
  return level === "doctor" || level === "aic" || level === "admin";
}

// Assignment rows that belong to the user, role-aware. Returns [] for none.
export function myRoomAssignments(assignments = [], user, staffList = []) {
  if (!user) return [];
  const ids = userStaffIds(user, staffList);
  const uname = norm(user.name);
  // The user's short code — directly on the user, else from a matched staff doc.
  const ucode =
    code(user.shortCode) ||
    code((staffList || []).find((s) => ids.has(s.id) && s.shortCode)?.shortCode);
  const matchesId = (id) => !!id && ids.has(id);
  const matchesName = (name) => !!uname && norm(name) === uname;
  const matchesCode = (c) => !!ucode && code(c) === ucode;

  if (isDoctorViewer(user)) {
    return (assignments || []).filter(
      (a) =>
        matchesId(a.attendingId) || matchesName(a.attendingName) || matchesCode(a.attendingShortCode) ||
        matchesId(a.mdReliefId) || matchesName(a.mdReliefName)
    );
  }
  return (assignments || []).filter(
    (a) =>
      matchesId(a.ahpId) || matchesName(a.ahpName) ||
      matchesId(a.crnaReliefId) || matchesName(a.crnaReliefName)
  );
}
